import { type ProjectActionItem } from '@consigli/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatStatusLabel } from '../components/action-items-table';
import { getPersonGroupKeyAndDisplay } from '../components/action-items-table-utils';

export interface FilterOptions {
  statuses: { value: string; label: string }[];
  people: { value: string; label: string }[];
  topics: { value: string; label: string }[];
}

export interface UseActionItemsFiltersReturn {
  // Filter state
  searchText: string;
  statusFilters: string[];
  personFilters: string[];
  topicFilters: string[];
  // Setters
  setSearchText: (value: string) => void;
  setStatusFilters: (values: string[]) => void;
  setPersonFilters: (values: string[]) => void;
  setTopicFilters: (values: string[]) => void;
  // Handlers
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearFilters: () => void;
  // Derived data
  filterOptions: FilterOptions;
  filteredItems: ProjectActionItem[];
  hasActiveFilters: boolean;
}

export const useActionItemsFilters = (
  data: ProjectActionItem[] | undefined,
): UseActionItemsFiltersReturn => {
  const { t } = useTranslation();

  // Filter state - now using arrays for multi-select
  const [searchText, setSearchText] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [personFilters, setPersonFilters] = useState<string[]>([]);
  const [topicFilters, setTopicFilters] = useState<string[]>([]);

  // Extract unique values for filter dropdowns
  const filterOptions = useMemo<FilterOptions>(() => {
    if (!data) return { statuses: [], people: [], topics: [] };

    const statuses = [...new Set(data.map((item) => item.completionStatus))].map((status) => ({
      value: status,
      label: formatStatusLabel(status),
    }));

    // Build people filter options with meeting-scoped grouping for generic speakers
    const peopleMap = new Map<string, string>();
    data.forEach((item) => {
      const { groupKey, displayName, subtitle } = getPersonGroupKeyAndDisplay(
        item.responsiblePerson?.name,
        item.meetingId,
        item.meetingTitle,
        t('meeting-secretary.action-items.unassigned'),
        item.meetingScheduledStartTime,
      );
      if (!peopleMap.has(groupKey)) {
        // For filter dropdown, combine name with subtitle if present
        const label = subtitle ? `${displayName} (${subtitle})` : displayName;
        peopleMap.set(groupKey, label);
      }
    });

    const people = Array.from(peopleMap.entries()).map(([value, label]) => ({
      value,
      label,
    }));

    const topics = [...new Set(data.map((item) => item.topic).filter(Boolean))].map((topic) => ({
      value: topic as string,
      label: topic as string,
    }));

    return { statuses, people, topics };
  }, [data, t]);

  // Filter items based on search and filters
  const filteredItems = useMemo(() => {
    if (!data) return [];

    return data.filter((item) => {
      // Search filter
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const matchesSearch =
          item.descriptionText.toLowerCase().includes(searchLower) ||
          item.responsiblePerson?.name?.toLowerCase().includes(searchLower) ||
          item.topic?.toLowerCase().includes(searchLower) ||
          item.meetingTitle?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Status filter - now supports multiple selections
      if (statusFilters.length > 0) {
        if (!statusFilters.includes(item.completionStatus)) return false;
      }

      // Person filter - uses meeting-scoped groupKey for generic speaker names
      if (personFilters.length > 0) {
        const { groupKey } = getPersonGroupKeyAndDisplay(
          item.responsiblePerson?.name,
          item.meetingId,
          item.meetingTitle,
          t('meeting-secretary.action-items.unassigned'),
          item.meetingScheduledStartTime,
        );
        if (!personFilters.includes(groupKey)) return false;
      }

      // Topic filter - now supports multiple selections
      if (topicFilters.length > 0) {
        if (!item.topic || !topicFilters.includes(item.topic)) return false;
      }

      return true;
    });
  }, [data, searchText, statusFilters, personFilters, topicFilters, t]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchText('');
    setStatusFilters([]);
    setPersonFilters([]);
    setTopicFilters([]);
  }, []);

  const hasActiveFilters = !!(
    searchText ||
    statusFilters.length > 0 ||
    personFilters.length > 0 ||
    topicFilters.length > 0
  );

  return {
    searchText,
    statusFilters,
    personFilters,
    topicFilters,
    setSearchText,
    setStatusFilters,
    setPersonFilters,
    setTopicFilters,
    handleSearchChange,
    clearFilters,
    filterOptions,
    filteredItems,
    hasActiveFilters,
  };
};
